import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

const STATUS_LABEL = { draft: "Bozza", pending_review: "In verifica", more_information_required: "Integrazioni richieste", approved: "Approvata", rejected: "Rifiutata", suspended: "Sospesa" };
const STATUS_COLOR = { draft: "bg-zinc-600", pending_review: "bg-amber-500 text-black", more_information_required: "bg-orange-500 text-black", approved: "bg-[#00E676] text-black", rejected: "bg-red-600", suspended: "bg-red-800" };

export function StripeTestBanner() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/payments/config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  if (!cfg?.show_test_banner) return null;
  return <div data-testid="stripe-test-banner" className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 px-4 py-2 text-sm">⚠️ Pagamenti in <b>Ambiente di test</b> (Stripe Test) — nessun addebito reale.</div>;
}

export default function BusinessVerification() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending_review");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const load = async () => {
    const params = {}; if (status) params.status = status; if (q) params.q = q;
    try { const { data } = await api.get("/admin/businesses", { params }); setItems(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const act = async (id, action, force = false) => {
    if (["reject", "suspend", "request_info"].includes(action) && !reason.trim()) { toast.error("Inserisci una motivazione"); return; }
    try { await api.put(`/admin/businesses/${id}/status`, { action, reason, force }); toast.success("Stato aggiornato"); setReason(""); setSel(null); load(); }
    catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}\n\nVuoi pubblicare comunque?`)) { act(id, action, true); }
        return;
      }
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const saveNotes = async (id) => { try { await api.put(`/admin/businesses/${id}/notes`, { notes }); toast.success("Note salvate"); } catch (e) { toast.error("Errore"); } };
  return (
    <div className="space-y-4">
      <StripeTestBanner />
      <h2 className="text-xl font-bold">Verifica attività</h2>
      <div className="flex gap-2 flex-wrap items-center">
        {Object.keys(STATUS_LABEL).map((s) => (
          <button key={s} data-testid={`vf-filter-${s}`} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-xs ${status === s ? "bg-[#00E676] text-black" : "bg-[#1C1C1F] text-zinc-300"}`}>{STATUS_LABEL[s]}</button>
        ))}
        <Input data-testid="vf-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca nome / P.IVA…" className="bg-[#1C1C1F] border-white/10 h-8 w-44" />
      </div>
      <div className="text-zinc-400 text-sm" data-testid="vf-count">{items.length} attività</div>
      <div className="space-y-3">
        {items.map((b) => (
          <div key={b.id} data-testid={`vf-card-${b.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
            <div className="flex justify-between items-start gap-3">
              <div className="flex items-center gap-3">
                {b.image ? <img src={b.image} alt="logo" data-testid={`vf-logo-${b.id}`} className="h-12 w-12 rounded-md object-cover border border-white/10" /> : <div className="h-12 w-12 rounded-md border border-dashed border-white/15 flex items-center justify-center text-[10px] text-zinc-600">no logo</div>}
                <div><div className="font-semibold">{b.brand_name || b.name}</div><div className="text-xs text-zinc-400">{b.legal_name || "—"} · P.IVA/CF {b.vat_or_tax || "—"}</div></div>
              </div>
              <Badge className={STATUS_COLOR[b.status]}>{STATUS_LABEL[b.status]}</Badge>
            </div>
            <div className="text-sm text-zinc-400 mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              <div>Referente: {b.contact_name || "—"}</div><div>Tel: {b.phone || "—"}</div>
              <div>Comune: {b.zone || "—"}</div><div>CAP: {b.cap || "—"}</div>
              <div>Categoria: {b.category || "—"}</div><div>Prodotti: {b.products_count}</div>
              <div className="sm:col-span-2">Indirizzo: {b.address || "—"}</div>
              <div className="sm:col-span-2">Orari: {b.opening_hours || "—"}</div>
              {b.description && <div className="sm:col-span-2">Descrizione: {b.description}</div>}
              {b.consent && <div className="sm:col-span-2 text-zinc-500 text-xs">Consenso accettato il {(b.consent.accepted_at || "").slice(0, 19).replace("T", " ")} · doc v{b.consent.doc_version} · IP {b.consent.ip || "n/d"}</div>}
              {b.approved_by && <div className="sm:col-span-2 text-[#00E676]">Approvata da {b.approved_by} il {(b.approved_at || "").slice(0, 10)}</div>}
              {b.rejection_reason && <div className="text-red-400 sm:col-span-2">Motivo: {b.rejection_reason}</div>}
              {b.internal_notes && <div className="text-zinc-500 sm:col-span-2">Note interne: {b.internal_notes}</div>}
            </div>
            {sel === b.id ? (
              <div className="mt-3 space-y-2">
                <Textarea data-testid={`vf-reason-${b.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivazione (obbligatoria per rifiuto / sospensione / integrazioni)" className="bg-[#0A0A0C] border-white/10" />
                <Textarea data-testid={`vf-notes-${b.id}`} defaultValue={b.internal_notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note interne amministrative" className="bg-[#0A0A0C] border-white/10" />
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" data-testid={`vf-approve-${b.id}`} onClick={() => act(b.id, "approve")} className="bg-[#00E676] text-black">Approva</Button>
                  <Button size="sm" data-testid={`vf-reqinfo-${b.id}`} onClick={() => act(b.id, "request_info")} variant="secondary">Richiedi integrazioni</Button>
                  <Button size="sm" data-testid={`vf-reject-${b.id}`} onClick={() => act(b.id, "reject")} className="bg-red-600">Rifiuta</Button>
                  <Button size="sm" data-testid={`vf-suspend-${b.id}`} onClick={() => act(b.id, "suspend")} className="bg-red-800">Sospendi</Button>
                  <Button size="sm" data-testid={`vf-reactivate-${b.id}`} onClick={() => act(b.id, "reactivate")} variant="outline">Riattiva</Button>
                  <Button size="sm" variant="ghost" onClick={() => saveNotes(b.id)}>Salva note</Button>
                  <Button size="sm" variant="ghost" onClick={() => setSel(null)}>Chiudi</Button>
                </div>
              </div>
            ) : <Button size="sm" data-testid={`vf-manage-${b.id}`} onClick={() => { setSel(b.id); setReason(""); setNotes(b.internal_notes || ""); }} className="mt-3 bg-[#00E676] text-black">Gestisci</Button>}
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 text-sm py-8 text-center">Nessuna attività in questo stato.</div>}
      </div>
    </div>
  );
}
